import { useMemo } from 'react'
import type { AbstractState, AsyncOptions, AsyncState } from 'react-async'
import { useAsync } from 'react-async'
import { useLocation } from 'react-router-dom'
import type { LoadPageModel } from '../services'
import { Services } from '../services'
import type { PageResponse } from '../services/models/pageResponse'

/**

 /**
 * Custom hook to load page components asynchronously while providing an initial default state.
 *
 * Example usage:
 *
 * ```typescript
 * const { pageResponse, isPending } = useLoadPageComponents(new AccountHomePageResponse(), PagesEnum.AccountHome, {
 *     friendlyUrl: '/some-page-url',
 *     usePageUrl: boolean // use 'PageEnums.AccountHome' to derive the URL for fetching components
 * });
 * ```
 *
 * @template T - The specific type extending PageResponse for the response data.
 * @param initData - The initial default data for the page response.
 * @param pageEnum - (Optional) A key from `Services.NavigationService.PagesEnum` to specify the page type.
 * @param options - (Optional) Additional configuration for the `useAsync` hook, including:
 *      - `friendlyUrl` (string): The URL to use for fetching data. Defaults to the current pathname if not provided.
 *      - `usePageUrl` (boolean): Determines whether the provided enum key is used to get the page URL from CMS paths to fetch components.
 * @returns {PageData<T>}
 * - `pageResponse`: The updated or default page response object.
 * - Other properties provided by `useAsync`, such as `isPending`, `error`, etc.
 *
 * Notes:
 * - The `useMemo` hook is used to avoid unnecessary re-renders by preserving the prototype of the initial object.
 * - Merges the `data` received from `useAsync` with the provided `initData` while ensuring functions and getters in `initData` are maintained.
 */

export const useLoadPageComponents = <T extends PageResponse>(
    initData: T,
    pageEnum?: keyof typeof Services.NavigationService.PagesEnum,
    options?: Options<T>
): PageData<T> => {
    const { pathname } = useLocation()

    const { data, ...rest } = useAsync<T>(Services.CmsService.loadPageComponents, {
        data: initData,
        page: pageEnum as unknown,
        friendlyUrl: pathname,
        ...options,
    })

    // This prevents unnecessary re-rendering in pages
    const pageResponse = useMemo(() => {
        const updatedObj = { ...initData, ...data } as T

        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment --  to preserve functions and getters to a new object
        updatedObj['__proto__'] = initData['__proto__']
        return updatedObj
    }, [data])

    return { pageResponse, ...rest } as PageData<T>
}

type PageData<T extends PageResponse> = (LoadedPageData<T> | PendingPageData<T>) &
    Omit<AsyncState<T, AbstractState<T>>, 'data'>

interface LoadedPageData<T extends PageResponse> extends BasePageData<T> {
    isPending: false
    pageResponse: T
}

interface PendingPageData<T extends PageResponse> extends BasePageData<T> {
    isPending: true
    pageResponse: T
}

interface BasePageData<T extends PageResponse> {
    isPending: boolean
    pageResponse: T | undefined
}

// actual type is not relevant in this context, `never` is used for readability.
interface Options<T> extends AsyncOptions<T>, Pick<LoadPageModel<never>, 'friendlyUrl' | 'usePageUrl'> {}
